import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight } from 'lucide-react';
import moffattranchWebp from '../assets/moffattranch.webp';
import moffattranch from '../assets/moffattranch.png';
import ravenscroftlogoWebp from '../assets/ravenscroftlogo.webp';
import ravenscroftlogo from '../assets/ravenscroftlogo.png';
import lezfindrBannerWebp from '../assets/LezFindr Banner Logo.webp';
import lezfindrBanner from '../assets/LezFindr Banner Logo.png';
import neeshLogoWebp from '../assets/neesh-logo.webp';
import neeshLogo from '../assets/neesh-logo.png';

const Portfolio = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const portfolioItems = [
    {
      title: 'Moffatt Ranch',
      problem: "A family farm since 1955 had no online presence. Visitors called constantly for hours and event info, and younger customers couldn't find them at all.",
      solution: "Built a fast, mobile-friendly website that became their primary marketing channel — now visitors find event info instantly instead of calling.",
      outcome: "Primary discovery channel for seasonal U-Pick events",
      image: moffattranch,
      imageWebp: moffattranchWebp,
      links: [{ text: 'Visit website', url: 'https://moffattranchbrentwood.com' }],
    },
    {
      title: 'Ravenscroft Design',
      problem: "A custom jewelry designer was stuck on Squarespace — slow page loads, no creative control, and paying monthly for a site that didn't reflect her work.",
      solution: "Built a fully custom portfolio with her own CMS. Page loads dropped from 3-5 seconds to under 1 second. She updates her own portfolio in minutes.",
      outcome: "Freed from platform lock-in with full ownership of her site",
      image: ravenscroftlogo,
      imageWebp: ravenscroftlogoWebp,
      links: [{ text: 'Visit website', url: 'https://ravenscroftdesign.com' }],
    },
    {
      title: 'Neesh',
      problem: "The founder built her marketplace prototype in Lovable but needed someone to make it production-ready — Supabase wiring, Stripe Connect for publisher payouts, and all the B2B workflows.",
      solution: "Took the Lovable-built prototype and got it fully wired up: Supabase backend, Stripe Connect integration for publisher payouts, checkout flows, and the full order-to-fulfillment pipeline.",
      outcome: "Live marketplace connecting publishers with design-forward retailers",
      image: neeshLogo,
      imageWebp: neeshLogoWebp,
      links: [{ text: 'Visit marketplace', url: 'https://neesh.art' }],
    },
    {
      title: 'LezFindr',
      problem: "A solo-built cross-platform mobile app shipped to both the iOS App Store and Google Play — with real-time chat, ML-powered verification, and multi-language support.",
      solution: "Built the entire product from scratch in Flutter with a Cloudflare Workers backend, on-device machine learning, and WebSocket messaging. Managed both app store submissions independently.",
      outcome: "Live on iOS and Android with active users worldwide",
      image: lezfindrBanner,
      imageWebp: lezfindrBannerWebp,
      links: [
        { text: 'Technical deep dive', url: '/lezfindr-showcase' },
        { text: 'Visit website', url: 'https://lezfindr.com' },
      ],
    },
  ];

  return (
    <motion.section
      ref={ref}
      id="portfolio"
      className="relative py-24 overflow-hidden"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 50 }}
      transition={{ duration: 0.8 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <h2 className="brand-header font-sans text-4xl font-extrabold sm:text-5xl">
            Real results for real businesses
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-lg text.body">
            Every project started with a problem. Here's how I solved them.
          </p>
        </div>

        <div className="space-y-24">
          {portfolioItems.map((item, index) => (
            <motion.div
              key={index}
              className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center"
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={{
                opacity: inView ? 1 : 0,
                x: inView ? 0 : (index % 2 === 0 ? -50 : 50),
              }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className={index % 2 === 0 ? 'md:order-1' : 'md:order-2'}>
                <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-md">
                  <picture>
                    <source srcSet={item.imageWebp} type="image/webp" />
                    <img
                      src={item.image}
                      alt={`${item.title} project screenshot`}
                      loading="lazy"
                      className="rounded-lg w-full h-auto object-cover transform hover:scale-105 transition-transform duration-300"
                    />
                  </picture>
                </div>
              </div>
              <div className={`${index % 2 === 0 ? 'md:order-2' : 'md:order-1'} bg-white p-8 rounded-xl shadow-md border border-gray-200`}>
                <h3 className="brand-subheader font-sans text-3xl font-bold mb-6">{item.title}</h3>
                <div className="space-y-5">
                  <div>
                    <h4 className="font-sans text-lg font-semibold mb-2 text.heading">The problem</h4>
                    <p className="font-sans text.body leading-relaxed">{item.problem}</p>
                  </div>
                  <div>
                    <h4 className="font-sans text-lg font-semibold mb-2 text.heading">What I built</h4>
                    <p className="font-sans text.body leading-relaxed">{item.solution}</p>
                  </div>
                  <div className="bg-purple-50 px-4 py-3 rounded-lg">
                    <p className="text-sm font-semibold text-purple-700">{item.outcome}</p>
                  </div>
                  {item.links && (
                    <div className="flex flex-wrap gap-4 mt-4">
                      {item.links.map((link, i) => (
                        <a
                          key={i}
                          href={link.url}
                          target={link.url.startsWith('/') ? undefined : '_blank'}
                          rel={link.url.startsWith('/') ? undefined : 'noopener noreferrer'}
                          className="inline-flex items-center text-purple-600 font-semibold hover:text-purple-700 transition-colors group"
                          aria-label={`${link.text} - ${item.title}`}
                        >
                          {link.text}
                          <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default Portfolio;
